import React, { useLayoutEffect, useRef, useState } from "react";
import MoreHorizIcon from "@material-ui/icons/MoreHoriz";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";

import { FB } from "../../assets/feedbackTheme";
import HeaderFilter from "./HeaderFilter";
import { countStage } from "./tracking";
import { StageIcon, StagePill, CountChip, OverallBadge, ProgressBar } from "./cells";

// İçerik Takip table. Every column header carries a sort toggle + HeaderFilter.
// When columns declare a `group` (Genel Takip tab) an extra header row labels the
// groups and a thick separator runs between them through every row.
//
// Freeze panes: columns flagged `frozen` (the leading Kazanım identity columns,
// each with a fixed `width`) stick to the left; the rest scroll horizontally under
// them. The "..." detail column sticks to the right. Sticky body cells use
// background "inherit" so they stay opaque and still follow the row's zebra/hover.

const GROUP_ROW_HEIGHT = 30;
const ACTION_COL_WIDTH = 44;
const SEPARATOR = `2px solid ${FB.borderStrong}`;
const HEADER_SEPARATOR = `2px solid ${FB.accentBorder}`;
const FROZEN_SHADOW = "6px 0 8px -6px rgba(0,0,0,0.25)";
const ACTION_SHADOW = "-6px 0 8px -6px rgba(0,0,0,0.25)";

const styles = {
  scroll: {
    height: "calc(100vh - 380px)",
    minHeight: 320,
    overflow: "auto",
    border: `1px solid ${FB.border}`,
    borderRadius: 12,
    backgroundColor: FB.surface,
  },
  table: { width: "100%", borderCollapse: "separate", borderSpacing: 0 },
  frozenText: { overflow: "hidden", overflowWrap: "anywhere" },
  groupCell: {
    position: "sticky",
    top: 0,
    zIndex: 6,
    height: GROUP_ROW_HEIGHT,
    padding: "0 12px",
    backgroundColor: FB.accent,
    color: FB.surface,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: 0.6,
    textTransform: "uppercase",
    textAlign: "center",
    whiteSpace: "nowrap",
    borderBottom: `1px solid ${FB.accentMid}`,
  },
  headCell: {
    position: "sticky",
    zIndex: 5,
    padding: "8px 8px",
    backgroundColor: FB.accent,
    color: FB.surface,
    fontSize: 12,
    fontWeight: 600,
    letterSpacing: 0.2,
    textAlign: "left",
    whiteSpace: "nowrap",
    verticalAlign: "middle",
  },
  headInner: (center) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: center ? "center" : "flex-start",
    gap: 6,
  }),
  sortBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 3,
    padding: 0,
    border: "none",
    background: "none",
    color: "inherit",
    font: "inherit",
    cursor: "pointer",
    textAlign: "inherit",
  },
  cell: {
    padding: "8px 8px",
    borderBottom: `1px solid ${FB.border}`,
    fontSize: 13,
    color: FB.text,
    verticalAlign: "middle",
  },
  actionBtn: {
    width: 32,
    height: 32,
    display: "inline-grid",
    placeItems: "center",
    padding: 0,
    border: "none",
    borderRadius: 8,
    backgroundColor: "transparent",
    color: FB.accent,
    cursor: "pointer",
  },
  progressBtn: {
    display: "block",
    width: "100%",
    padding: 4,
    border: "none",
    borderRadius: 8,
    background: "none",
    cursor: "pointer",
    textAlign: "left",
  },
  idChip: {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 6,
    fontSize: 12,
    fontWeight: 500,
    fontVariantNumeric: "tabular-nums",
    color: FB.textSecondary,
    backgroundColor: FB.surfaceAlt,
    border: `1px solid ${FB.border}`,
  },
  empty: { padding: 40, textAlign: "center", color: FB.textMuted, fontSize: 13 },
};

// Consecutive columns sharing a group → one spanning header cell.
const buildGroups = (columns) => {
  const groups = [];
  columns.forEach((col) => {
    const last = groups[groups.length - 1];
    if (last && last.label === col.group) {
      last.span += 1;
      last.width += col.width || 0;
    } else {
      groups.push({ label: col.group, span: 1, frozen: !!col.frozen, width: col.width || 0 });
    }
  });
  return groups;
};

// border-box so padding/borders stay inside the width (the sticky left offsets
// are plain sums of these widths).
const widthStyle = (col) =>
  col.width
    ? { width: col.width, minWidth: col.width, maxWidth: col.width, boxSizing: "border-box" }
    : null;

// Left offset of every frozen column; null for scrolling columns.
const frozenOffsets = (columns) => {
  let left = 0;
  return columns.map((col) => {
    if (!col.frozen) return null;
    const offset = left;
    left += col.width;
    return offset;
  });
};

const isGroupStart = (columns, index) =>
  index > 0 && columns[index].group !== columns[index - 1].group;

const renderCell = (col, row, onOpenDetail) => {
  switch (col.type) {
    case "stage":
      return <StageIcon stage={col.stage(row)} label={col.label} />;
    case "pill":
      return <StagePill stage={col.stage(row)} label={col.value(row)} />;
    case "count": {
      const value = col.pick(row);
      return <CountChip value={value} goal={col.goal} stage={countStage(value, col.goal, col.invert)} />;
    }
    case "overall":
      return <OverallBadge done={row._d.overallDone} />;
    case "progress":
      return (
        <button
          type="button"
          style={styles.progressBtn}
          aria-label={`${row.AchievementId} detayını aç`}
          onClick={() => onOpenDetail(row)}
        >
          <ProgressBar value={row._d.progress} />
        </button>
      );
    case "achievement":
      // Clamped to 3 lines to keep the table narrow; full text on hover + in the drawer.
      return (
        <div
          title={row[col.key]}
          style={{
            lineHeight: 1.35,
            fontWeight: 500,
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {row[col.key]}
        </div>
      );
    case "id":
      return <span style={styles.idChip}>{row[col.key]}</span>;
    default:
      return (
        <span
          style={{
            color: col.strong ? FB.text : FB.textSecondary,
            fontWeight: col.strong ? 600 : 400,
          }}
        >
          {col.text ? col.text(row) : row[col.key]}
        </span>
      );
  }
};

const TrackingTable = ({
  columns,
  rows,
  filterOptions,
  filters,
  onFilterChange,
  sort,
  onSort,
  onOpenDetail,
  emptyMessage,
}) => {
  const scrollRef = useRef(null);
  const [scrolled, setScrolled] = useState({ left: false, right: false });

  const grouped = columns.some((c) => c.group);
  const groups = grouped ? buildGroups(columns) : [];
  const headTop = grouped ? GROUP_ROW_HEIGHT : 0;
  const colCount = columns.length + 1;
  const offsets = frozenOffsets(columns);
  const lastFrozen = offsets.reduce((last, o, i) => (o != null ? i : last), -1);

  // Shadows only while something is actually hidden under the sticky edges.
  const updateScrolled = () => {
    const el = scrollRef.current;
    if (!el) return;
    const left = el.scrollLeft > 0;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
    setScrolled((prev) => (prev.left === left && prev.right === right ? prev : { left, right }));
  };

  useLayoutEffect(() => {
    updateScrolled();
    window.addEventListener("resize", updateScrolled);
    return () => window.removeEventListener("resize", updateScrolled);
  });

  // Sticky/separator styles shared by header and body cells of column i.
  const columnEdgeStyle = (i, header) => {
    const style = {};
    if (offsets[i] != null) {
      style.position = "sticky";
      style.left = offsets[i];
    }
    if (i === lastFrozen) {
      style.borderRight = header ? HEADER_SEPARATOR : SEPARATOR;
      if (scrolled.left) style.boxShadow = FROZEN_SHADOW;
    } else if (grouped && isGroupStart(columns, i) && i - 1 !== lastFrozen) {
      style.borderLeft = header ? HEADER_SEPARATOR : SEPARATOR;
    }
    return style;
  };

  const actionEdgeStyle = {
    position: "sticky",
    right: 0,
    width: ACTION_COL_WIDTH,
    minWidth: ACTION_COL_WIDTH,
    boxSizing: "border-box",
    boxShadow: scrolled.right ? ACTION_SHADOW : "none",
  };

  return (
    <div ref={scrollRef} style={styles.scroll} onScroll={updateScrolled}>
      <table style={styles.table}>
        <thead>
          {grouped && (
            <tr>
              {groups.map((g, i) => (
                <th
                  key={g.label}
                  colSpan={g.span}
                  style={{
                    ...styles.groupCell,
                    ...(g.frozen
                      ? {
                          left: 0,
                          zIndex: 8,
                          width: g.width,
                          minWidth: g.width,
                          maxWidth: g.width,
                          boxSizing: "border-box",
                          borderRight: HEADER_SEPARATOR,
                          boxShadow: scrolled.left ? FROZEN_SHADOW : "none",
                        }
                      : {}),
                    borderLeft: i > 0 && !groups[i - 1].frozen ? HEADER_SEPARATOR : "none",
                  }}
                >
                  {g.label}
                </th>
              ))}
              <th style={{ ...styles.groupCell, ...actionEdgeStyle, zIndex: 8 }} />
            </tr>
          )}
          <tr>
            {columns.map((col, i) => {
              const center = col.align === "center";
              const sorted = sort.key === col.key ? sort.dir : null;
              return (
                <th
                  key={col.key}
                  style={{
                    ...styles.headCell,
                    ...widthStyle(col),
                    ...columnEdgeStyle(i, true),
                    top: headTop,
                    zIndex: col.frozen ? 7 : 5,
                    textAlign: center ? "center" : "left",
                    // Narrow status columns wrap their label instead of widening.
                    whiteSpace: col.width ? "normal" : "nowrap",
                  }}
                >
                  <div style={styles.headInner(center)}>
                    <button
                      type="button"
                      style={styles.sortBtn}
                      title={`${col.label} sırala`}
                      onClick={() => onSort(col.key)}
                    >
                      {col.label}
                      {sorted === "asc" && <ArrowUpwardIcon style={{ fontSize: 13 }} />}
                      {sorted === "desc" && <ArrowDownwardIcon style={{ fontSize: 13 }} />}
                    </button>
                    <HeaderFilter
                      label={col.label}
                      options={filterOptions[col.key] || []}
                      selectedValues={filters[col.key] == null ? null : filters[col.key]}
                      onChange={(val) => onFilterChange(col.key, val)}
                    />
                  </div>
                </th>
              );
            })}
            <th style={{ ...styles.headCell, ...actionEdgeStyle, top: headTop, zIndex: 7 }}>
              <span style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0,0,0,0)" }}>
                Detay
              </span>
            </th>
          </tr>
        </thead>

        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={colCount} style={styles.empty}>
                {emptyMessage}
              </td>
            </tr>
          ) : (
            rows.map((row, rowIndex) => {
              const bg = rowIndex % 2 ? FB.surfaceAlt : FB.surface;
              return (
                <tr
                  key={row.AchievementId}
                  style={{ backgroundColor: bg }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = FB.accentSoft;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = bg;
                  }}
                >
                  {columns.map((col, i) => (
                    <td
                      key={col.key}
                      style={{
                        ...styles.cell,
                        ...widthStyle(col),
                        ...columnEdgeStyle(i, false),
                        ...(col.frozen ? { ...styles.frozenText, zIndex: 2, backgroundColor: "inherit" } : {}),
                        textAlign: col.align === "center" ? "center" : "left",
                      }}
                    >
                      {renderCell(col, row, onOpenDetail)}
                    </td>
                  ))}
                  <td
                    style={{
                      ...styles.cell,
                      ...actionEdgeStyle,
                      zIndex: 2,
                      backgroundColor: "inherit",
                      textAlign: "center",
                    }}
                  >
                    <button
                      type="button"
                      style={styles.actionBtn}
                      aria-label={`${row.AchievementId} detayını aç`}
                      onClick={() => onOpenDetail(row)}
                    >
                      <MoreHorizIcon />
                    </button>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};

export default TrackingTable;
